import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { PlayCircle, X, Clock3 } from "lucide-react";

/**
 * VideoLightbox — headless click-to-expand video modal.
 *
 * Props
 *  - src?         URL of the video. If it looks like a YouTube / Vimeo /
 *                 Loom watch URL we auto-render an iframe with autoplay.
 *                 If it's an mp4/webm we render <video controls autoplay>.
 *                 If undefined we render a friendly "Coming soon" card so
 *                 the placeholder ships now and gets swapped later.
 *  - title
 *  - open, onClose
 */
export default function VideoLightbox({ src, title, open, onClose }) {
  const embed = useEmbedUrl(src);
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  const isEmbed = embed?.type === "iframe";
  const isFile = embed?.type === "file";
  const body = (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Video overview"}
      data-testid="video-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
        data-testid="video-lightbox-close"
      >
        <X size={20} />
      </button>
      <div
        className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        {isEmbed ? (
          <iframe
            src={embed.url}
            title={title || "VenturaZ overview"}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            data-testid="video-lightbox-iframe"
          />
        ) : isFile ? (
          <video
            src={embed.url}
            className="w-full h-full"
            controls
            autoPlay
            playsInline
            data-testid="video-lightbox-video"
          />
        ) : (
          <ComingSoonCard title={title} />
        )}
      </div>
    </div>
  );
  return createPortal(body, document.body);
}

// -------------------------------------------------------------------
// Placeholder body when no `src` has been supplied yet.
// -------------------------------------------------------------------
function ComingSoonCard({ title }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#0F3D2E] via-[#183B36] to-[#0D2A24] text-cream text-center px-6" data-testid="video-lightbox-placeholder">
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <ShimmerPattern />
      </div>
      <div className="relative">
        <div className="inline-flex items-center gap-2 border border-white/25 bg-white/[0.06] rounded-full px-3 py-1 mb-4 text-[10px] font-mono uppercase tracking-widest text-gold/90">
          <Clock3 size={11} /> Coming shortly
        </div>
        <h3 className="font-heading text-3xl leading-tight max-w-xl mx-auto">{title || "VenturaZ · in 90 seconds"}</h3>
        <p className="text-cream/70 text-sm max-w-lg mx-auto mt-3">
          We&apos;re polishing the walkthrough. In the meantime, dive straight in — the platform explains itself in under two clicks.
        </p>
      </div>
    </div>
  );
}

// A slow copper-tinted shimmer used behind the placeholder card. Pure
// CSS so it costs zero extra JS.
function ShimmerPattern() {
  return (
    <svg viewBox="0 0 400 225" preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id="vlx-shimmer" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(212,175,55,0)" />
          <stop offset="50%" stopColor="rgba(212,175,55,0.35)" />
          <stop offset="100%" stopColor="rgba(212,175,55,0)" />
        </linearGradient>
      </defs>
      {[...Array(6)].map((_, i) => (
        <rect key={i} x={-200 + i * 90} y="0" width="60" height="225" fill="url(#vlx-shimmer)">
          <animate attributeName="x" from={-200} to="600" dur={`${6 + i}s`} repeatCount="indefinite" begin={`${i * 0.6}s`} />
        </rect>
      ))}
    </svg>
  );
}

// -------------------------------------------------------------------
// URL sniffing — turn the incoming `src` into either an embeddable
// iframe URL or a direct file URL.
// -------------------------------------------------------------------
function useEmbedUrl(src) {
  return React.useMemo(() => {
    if (!src) return null;
    const s = String(src).trim();
    if (/\.(mp4|webm|mov)$/i.test(s)) return { type: "file", url: s };
    let m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
    if (m) return { type: "iframe", url: `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` };
    m = s.match(/vimeo\.com\/(\d+)/);
    if (m) return { type: "iframe", url: `https://player.vimeo.com/video/${m[1]}?autoplay=1` };
    m = s.match(/loom\.com\/share\/([\w-]+)/);
    if (m) return { type: "iframe", url: `https://www.loom.com/embed/${m[1]}?autoplay=1` };
    if (/^https?:\/\//.test(s)) return { type: "iframe", url: s };
    return null;
  }, [src]);
}

// -------------------------------------------------------------------
// VideoPreview — thumbnail that opens the lightbox. Two sizes.
// -------------------------------------------------------------------
export function VideoPreview({
  src,
  title = "VenturaZ · in 90 seconds",
  duration = "1:32",
  eyebrow = "Product tour",
  description,
  size = "banner", // "banner" | "thumb"
  palette = "navy", // "navy" | "copper"
  testId = "video-preview",
}) {
  const [open, setOpen] = useState(false);
  const handle = useCallback(() => setOpen(true), []);
  const isCopper = palette === "copper";
  return (
    <>
      {size === "thumb" ? (
        <button
          type="button"
          onClick={handle}
          className={`group inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-heading transition-all border ${
            isCopper
              ? "border-[#B8860B]/50 bg-[#7A4E1D]/15 text-[#8A6600] hover:bg-[#B8860B]/[0.18]"
              : "border-white/25 bg-white/[0.08] text-cream hover:bg-white/[0.14]"
          }`}
          data-testid={testId}
          aria-label={`Play ${title}`}
        >
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-transform group-hover:scale-110 ${
            isCopper ? "bg-[#B8860B] text-white" : "bg-gold text-navy"
          }`}>
            <PlayCircle size={16} strokeWidth={2} />
          </span>
          <span className="truncate">Watch · {duration}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={handle}
          className={`group relative block w-full overflow-hidden rounded-2xl border transition-all shadow-[0_25px_60px_-25px_rgba(15,61,46,0.35)] hover:shadow-[0_30px_70px_-25px_rgba(15,61,46,0.45)] ${
            isCopper ? "border-[#B8860B]/35" : "border-navy/15"
          }`}
          data-testid={testId}
          aria-label={`Play ${title}`}
        >
          {/* Poster canvas — gradient + concentric rings + running shimmer */}
          <div className="aspect-video w-full relative">
            <div
              className="absolute inset-0"
              style={{
                background: isCopper
                  ? "linear-gradient(135deg, #2E1810 0%, #5C2E15 45%, #7A4E1D 100%)"
                  : "linear-gradient(135deg, #0F3D2E 0%, #14322A 45%, #0A2620 100%)",
              }}
            />
            <div className="absolute inset-0 opacity-30">
              <ShimmerPattern />
            </div>
            <svg aria-hidden="true" className="absolute -left-16 -top-16 w-[380px] h-[380px] opacity-25" viewBox="0 0 400 400">
              {[60, 90, 120, 150, 180, 210, 240].map((r) => (
                <circle key={r} cx="200" cy="200" r={r} fill="none" stroke={isCopper ? "#F5C97A" : "#FFD700"} strokeWidth="1" opacity="0.45" />
              ))}
            </svg>
            <svg aria-hidden="true" className="absolute -right-24 -bottom-24 w-[420px] h-[420px] opacity-20" viewBox="0 0 400 400">
              {[0, 40, 80, 120, 160].map((x, i) => (
                <g key={i} transform={`translate(${x} 100) rotate(45 60 60)`}>
                  <rect x="0" y="0" width="120" height="120" fill="none" stroke={isCopper ? "#F5C97A" : "#FFD700"} strokeWidth="1" opacity={0.6 - i * 0.1} />
                </g>
              ))}
            </svg>

            {/* Content */}
            <div className="absolute inset-0 flex flex-col justify-between p-6 sm:p-8 text-left">
              <div className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] ${
                isCopper ? "text-[#F5C97A]" : "text-gold/90"
              }`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isCopper ? "bg-[#F5C97A]" : "bg-gold"} animate-pulse`} />
                {eyebrow}
              </div>
              <div className="max-w-xl">
                <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-cream leading-tight">{title}</h3>
                {description && <p className="mt-2 text-sm sm:text-base text-cream/75 leading-relaxed max-w-md">{description}</p>}
                <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-cream/70">
                  <Clock3 size={11} className={isCopper ? "text-[#F5C97A]" : "text-gold"} />
                  {duration}
                </div>
              </div>
            </div>

            {/* Center play button */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                isCopper
                  ? "bg-[#B8860B] shadow-[0_10px_30px_rgba(184,134,11,0.55)]"
                  : "bg-gold shadow-[0_10px_30px_rgba(212,175,55,0.55)]"
              }`}>
                <PlayCircle size={40} className="text-navy" strokeWidth={1.6} />
              </div>
            </div>
          </div>
        </button>
      )}
      <VideoLightbox src={src} title={title} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
